/**
 * Continuity Plan Data Mapper
 * 
 * Maps PlanBuilder responses (using options.key from YAML) to the nested
 * structure expected by generateBCPDocument().
 * 
 * YAML keys use patterns like:
 * - contact1_name → emergencyContacts.contact1.name
 * - clearingFirm_name → businessDescription.clearingFirm.name
 * - operatingBank_name → banks.operatingBank.name
 * - locations (repeater) → officeLocations.locations
 */

/**
 * Build answer map from progress for quick lookup
 */
const buildAnswerMap = (progress) => {
  const answerMap = {};
  const questionKeyMap = {};
  
  // First pass: map question IDs to their options.key
  progress?.planTemplate?.planModules?.forEach(module => {
    module.planSections?.forEach(section => {
      section.planQuestions?.forEach(question => {
        const key = question.options?.key;
        if (key) {
          questionKeyMap[question.id] = key;
        }
      });
    });
  });
  
  // Second pass: map keys to answer values
  progress?.userPlanResponses?.forEach(response => {
    const key = questionKeyMap[response.planQuestionId];
    if (key) {
      answerMap[key] = response.answer?.value;
    }
  });
  
  return answerMap;
};

/**
 * Maps PlanBuilder progress to the formData structure expected by
 * generateBCPDocument() from bcp-template-generator.js
 * 
 * @param {Object} progress - The userPlanProgress from GraphQL
 * @returns {Object} formData - Structured data for the BCP generator
 */
export const mapPlanBuilderToBCPFormat = (progress) => {
  const answers = buildAnswerMap(progress);
  
  const formData = {
    // Company Information
    companyInfo: {
      firmName: answers.firmName || ''
    },
    
    // Firm Policy
    firmPolicy: {
      policyStatement: answers.policyStatement || '',
      planApprover: answers.planApprover || '',
      planLocation: answers.planLocation || ''
    },
    
    // Emergency Contacts
    emergencyContacts: {
      contact1: {
        name: answers.contact1_name || '',
        title: answers.contact1_title || '',
        mailing: answers.contact1_mailing || '',
        email: answers.contact1_email || '',
        phone: answers.contact1_phone || ''
      },
      contact2: {
        name: answers.contact2_name || '',
        title: answers.contact2_title || '',
        mailing: answers.contact2_mailing || '',
        email: answers.contact2_email || '',
        phone: answers.contact2_phone || ''
      },
      fcsResponsible: answers.fcsResponsible || ''
    },
    
    // Business Description
    businessDescription: {
      businessTypes: answers.businessTypes || [],
      otherBusinessTypes: answers.otherBusinessTypes || '',
      customerTypes: answers.customerTypes || '',
      clearingFirm: {
        name: answers.clearingFirm_name || '',
        address: answers.clearingFirm_address || '',
        phone: answers.clearingFirm_phone || '',
        email: answers.clearingFirm_email || '',
        website: answers.clearingFirm_website || '',
        primaryContact: answers.clearingFirm_primaryContact || '',
        primaryContactPhone: answers.clearingFirm_primaryContactPhone || '',
        primaryContactEmail: answers.clearingFirm_primaryContactEmail || '',
        alternateContact: answers.clearingFirm_alternateContact || '',
        alternateContactPhone: answers.clearingFirm_alternateContactPhone || '',
        alternateContactEmail: answers.clearingFirm_alternateContactEmail || ''
      }
    },
    
    // Office Locations (repeater field)
    officeLocations: {
      locations: answers.locations || []
    },
    
    // Alternate Physical Locations
    alternateLocations: {
      alternateLocationStrategy: answers.alternateLocationStrategy || '',
      remoteWorkCybersecurity: answers.remoteWorkCybersecurity || [],
      otherCybersecurityMeasures: answers.otherCybersecurityMeasures || ''
    },
    
    // Customer Access to Funds and Securities
    customerAccess: {
      customerAccessMethod: answers.customerAccessMethod || [],
      otherCustomerAccessMethod: answers.otherCustomerAccessMethod || '',
      clearingFirmCustomerContact: answers.clearingFirmCustomerContact || ''
    },
    
    // Data Back-Up and Recovery
    dataBackup: {
      primaryRecordsFormat: answers.primaryRecordsFormat || '',
      primaryRecordsLocation: answers.primaryRecordsLocation || '',
      primaryRecordsResponsible: answers.primaryRecordsResponsible || '',
      exclusiveDocuments: answers.exclusiveDocuments || '',
      backupRecordsFormat: answers.backupRecordsFormat || '',
      backupRecordsLocation: answers.backupRecordsLocation || '',
      backupRecordsResponsible: answers.backupRecordsResponsible || '',
      paperBackupFrequency: answers.paperBackupFrequency || '',
      paperBackupProcess: answers.paperBackupProcess || '',
      electronicBackupFrequency: answers.electronicBackupFrequency || '',
      electronicBackupProcess: answers.electronicBackupProcess || '',
      usesRecordkeepingService: answers.usesRecordkeepingService || '',
      recordkeepingService: {
        name: answers.recordkeepingService_name || '',
        address: answers.recordkeepingService_address || '',
        phone: answers.recordkeepingService_phone || ''
      }
    },
    
    // Operational Risk
    operationalRisk: {
      communicationMethods: answers.communicationMethods || [],
      otherCommunicationMethods: answers.otherCommunicationMethods || '',
      customerNotification: answers.customerNotification || [],
      otherCustomerNotification: answers.otherCustomerNotification || ''
    },
    
    // Financial and Credit Risk
    financialCreditRisk: {
      financialProcedures: answers.financialProcedures || ''
    },
    
    // Mission Critical Systems
    missionCriticalSystems: {
      criticalSystems: answers.criticalSystems || [],
      orderTakingMethods: answers.orderTakingMethods || [],
      otherOrderTakingMethods: answers.otherOrderTakingMethods || '',
      orderEntrySystem: answers.orderEntrySystem || '',
      orderEntryBackup: answers.orderEntryBackup || '',
      alternateOrderEntryMethods: answers.alternateOrderEntryMethods || '',
      orderExecutionMethods: answers.orderExecutionMethods || '',
      otherServices: answers.otherServices || '',
      clearingFirmServices: answers.clearingFirmServices || []
    },
    
    // Alternate Communications with Customers
    alternateCustomerCommunication: {
      customerCommMethods: answers.customerCommMethods || [],
      otherCustomerCommMethods: answers.otherCustomerCommMethods || ''
    },
    
    // Alternate Communications with Employees
    alternateEmployeeCommunication: {
      employeeCommMethods: answers.employeeCommMethods || [],
      otherEmployeeCommMethods: answers.otherEmployeeCommMethods || '',
      callTreeResponsible: answers.callTreeResponsible || '',
      callTree: answers.callTree || []
    },
    
    // Communications with Regulators
    regulatorCommunication: {
      regulators: answers.regulators || [],
      regulatorCommMethods: answers.regulatorCommMethods || [],
      otherRegulatorCommMethods: answers.otherRegulatorCommMethods || ''
    },
    
    // Business Constituents
    businessConstituents: {
      criticalBusinessConstituents: answers.criticalBusinessConstituents || []
    },
    
    // Banks
    banks: {
      operatingBank: {
        name: answers.operatingBank_name || '',
        address: answers.operatingBank_address || '',
        phone: answers.operatingBank_phone || '',
        contact: answers.operatingBank_contact || ''
      },
      paibBank: {
        name: answers.paibBank_name || '',
        address: answers.paibBank_address || '',
        phone: answers.paibBank_phone || '',
        contact: answers.paibBank_contact || ''
      },
      alternateFinancing: {
        name: answers.alternateFinancing_name || '',
        address: answers.alternateFinancing_address || '',
        phone: answers.alternateFinancing_phone || ''
      }
    },
    
    // Regulatory Reporting
    regulatoryReporting: {
      regulatoryFilingMethods: answers.regulatoryFilingMethods || [],
      otherRegulatoryFilingMethods: answers.otherRegulatoryFilingMethods || '',
      regulatoryContacts: answers.regulatoryContacts || []
    },
    
    // Customer Disclosure
    customerDisclosure: {
      disclosureMethods: answers.disclosureMethods || [],
      otherDisclosureMethods: answers.otherDisclosureMethods || ''
    },
    
    // Updates and Annual Review
    updating: {
      annualReviewDate: answers.annualReviewDate || '',
      conductsTest: answers.conductsTest || ''
    },
    
    // Senior Manager Approval
    seniorManagerApproval: {
      approverName: answers.approverName || '',
      approverTitle: answers.approverTitle || '',
      approvalDate: answers.approvalDate || ''
    },
    
    // BCP Disclosure Statement
    disclosureStatement: {
      firmEmergencyNumber: answers.firmEmergencyNumber || '',
      websiteURL: answers.websiteURL || '',
      customerContactInfo: answers.customerContactInfo || '',
      clearingFirmServices: answers.clearingFirmServices_disclosure || [],
      otherClearingFirmServices: answers.otherClearingFirmServices || '',
      resumptionTimePeriod: answers.resumptionTimePeriod || '',
      additionalDisclosureInfo: answers.additionalDisclosureInfo || ''
    }
  };
  
  return formData;
};

export default mapPlanBuilderToBCPFormat;


